/* AdSlot — renders a placement by slug.
   - If filled: shows advertiser creative + click-through (opens in new tab)
   - If unfilled: shows "Your brand here" CTA with mailto link to info@
   Impressions are tracked once per mount. */
import React, { useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { Megaphone, ExternalLink } from 'lucide-react';
import AdSenseBanner from '@/components/AdSenseBanner';
import { useAuth } from '@/contexts/AuthContext';
import { isPaidSubscriber } from '@/lib/subscription';

const MAILTO = (placement, email) => {
    const subject = encodeURIComponent(`Advertising enquiry: ${placement.name}`);
    const body = encodeURIComponent(
        `Hi Linguistic Quest team,\n\n` +
        `I'd like to enquire about advertising in the "${placement.name}" slot.\n\n` +
        `Placement: ${placement.location}\n` +
        `Visibility: ${placement.visibility_description}\n` +
        `Format: ${placement.format}\n\n` +
        `My brand/website: \n` +
        `Campaign dates: \n` +
        `Best, `
    );
    return `mailto:${email}?subject=${subject}&body=${body}`;
};

export default function AdSlot({ slug, className = '', variant = 'banner' }) {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const tracked = useRef(false);

    // Paid plans are 100% ad-free. Skip the slot entirely for subscribers.
    const adFree = isPaidSubscriber(user);

    useEffect(() => {
        let ok = true;
        api.get(`/ads/placements/${slug}`).then(({ data }) => {
            if (ok) setData(data);
        }).catch(() => { if (ok) setErr('ad-not-found'); });
        return () => { ok = false; };
    }, [slug]);

    useEffect(() => {
        if (!data || tracked.current) return;
        tracked.current = true;
        api.post(`/ads/placements/${slug}/impression`).catch(() => {});
    }, [data, slug]);

    if (adFree) return null;
    if (err || !data) return null;

    const p = data.placement;
    const email = data.enquiry_email;
    const compact = variant === 'compact';

    // FILLED — real advertiser
    if (p.is_filled && p.advertiser_url) {
        const onClick = (e) => {
            // let the link open normally, but also log click
            api.post(`/ads/placements/${slug}/click`).catch(() => {});
        };
        const bg = p.bg_color || '#0f172a';
        const fg = p.text_color || '#fef3c7';
        return (
            <a href={p.advertiser_url} target="_blank" rel="noopener noreferrer sponsored"
               onClick={onClick}
               data-testid={`ad-slot-${slug}-filled`}
               className={`block rounded-2xl overflow-hidden border border-lq-ink-100 shadow-sm hover:shadow-md transition-shadow ${className}`}
               style={{ background: bg, color: fg }}>
                {p.creative_image_url && (
                    <img src={p.creative_image_url} alt={p.advertiser_name || 'Advertiser'} className="w-full h-auto max-h-[420px] object-contain mx-auto block" loading="lazy" decoding="async" />
                )}
                {!p.creative_image_url && (
                    <div className="px-6 py-8 md:py-10">
                        <div className="text-xs uppercase tracking-wider opacity-70">Sponsored</div>
                        <div className="mt-1 font-display text-2xl md:text-3xl font-semibold">{p.advertiser_name}</div>
                        {p.advertiser_tagline && <div className="mt-2 text-sm md:text-base opacity-80 max-w-xl">{p.advertiser_tagline}</div>}
                        <div className="mt-4 inline-flex items-center gap-1 text-sm font-semibold">Visit <ExternalLink size={14}/></div>
                    </div>
                )}
                <div className="px-4 py-1 text-[10px] uppercase tracking-wider opacity-60 text-right bg-black/20">Ad</div>
            </a>
        );
    }

    // EMPTY — "advertise with us" CTA + AdSense fallback
    return (
        <div className={className} data-testid={`ad-slot-${slug}-empty-wrapper`}>
            <a href={MAILTO(p, email)}
               data-testid={`ad-slot-${slug}-empty`}
               className={`group block rounded-2xl border-2 border-dashed border-lq-ink-300 bg-lq-paper hover:border-lq-purple-500 hover:bg-lq-purple-50 transition-colors overflow-hidden`}>
                <div className="grid md:grid-cols-[1.4fr,1fr]">
                    <div className="relative">
                        <img
                            src="/brand/ad_example_learning_together.jpg"
                            alt="Example placement: a family solving puzzles together with the headline Learning Together, Growing Together"
                            className="w-full h-full object-cover block"
                            loading="lazy"
                            decoding="async"
                            width="1400" height="787"
                        />
                        <span className="absolute top-3 left-3 text-[10px] uppercase tracking-wider bg-black/60 text-white px-2 py-0.5 rounded-full">Example placement</span>
                    </div>
                    <div className={`px-6 ${compact ? 'py-5' : 'py-8'} flex flex-col justify-center`}>
                        <div className="flex items-center gap-3">
                            <div className="w-11 h-11 rounded-xl bg-lq-purple-100 text-lq-purple-700 inline-flex items-center justify-center flex-shrink-0">
                                <Megaphone size={20} />
                            </div>
                            <div className="text-xs uppercase tracking-wider font-semibold text-lq-purple-500">Your brand here</div>
                        </div>
                        <div className={`mt-3 font-display ${compact ? 'text-lg' : 'text-xl md:text-2xl'} font-semibold text-lq-ink-900`}>{p.name}</div>
                        <div className="mt-1 text-sm text-lq-ink-700">{p.visibility_description}</div>
                        <div className="mt-2 text-xs text-lq-ink-500"><strong>Format:</strong> {p.format} · <strong>Click-through:</strong> {p.includes_hyperlink ? 'yes' : 'no'}</div>
                        <div className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-lq-purple-700 group-hover:text-lq-purple-900">
                            Email {email} to advertise <ExternalLink size={14}/>
                        </div>
                    </div>
                </div>
            </a>
            {/* AdSense fallback — Google fills the slot while it's unsold */}
            <div className="mt-3" aria-hidden="true">
                <AdSenseBanner testId={`adsense-fallback-${slug}`} />
            </div>
        </div>
    );
}
